
import React, { useEffect, useState } from "react";
import { getParticipantActivity } from "../../../services/analytics/analyticsService";
import { ParticipantActivityResponse } from "../../../interfaces/analytics";
import AnalyticsFilters from "./AnalyticsFilters";
import Pagination from "../utility/Pagination";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { useAuth } from "../../../firebase/AuthProvider";

const ParticipantActivityAnalytics: React.FC = () => {
  const { currentUser } = useAuth();
  const [authToken, setAuthToken] = useState<string>("");
  const [data, setData] = useState<ParticipantActivityResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  useEffect(() => {
    if (currentUser) {
      currentUser.getIdToken().then(token => setAuthToken(token));
    }
  }, [currentUser]);

  useEffect(() => {
    if (!authToken) return;

    const fetchData = async () => {
      try {
        setLoading(true);
        const response = await getParticipantActivity(authToken, filters);
        setData(response);
        setCurrentPage(1);
      } catch (err) {
        setError("Failed to fetch Participant Activity data.");
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [authToken, filters]);

  const handleFiltersChange = (newFilters: any) => {
    setFilters(newFilters);
  };

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = data ? data.rows.slice(indexOfFirstItem, indexOfLastItem) : [];
  const totalPages = data ? Math.ceil(data.rows.length / itemsPerPage) : 0;

  if (loading) {
    return <div className="text-center p-4">Loading Participant Activity data...</div>;
  }

  if (error) {
    return <div className="text-center p-4 text-red-500">{error}</div>;
  }

  return (
    <div className="p-4">
      <h2 className="text-xl font-bold mb-4">Participant Activity Analytics</h2>
      <AnalyticsFilters filters={filters} onFiltersChange={handleFiltersChange} />
      {data ? (
        <>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={data.rows}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="participantFullName" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Bar dataKey="recentCompletions30d" fill="#ffc658" name="Recent Completions (30d)" />
            </BarChart>
          </ResponsiveContainer>
          <div className="overflow-x-auto mt-4">
            <table className="min-w-full bg-white border">
              <thead>
                <tr>
                  <th className="py-2 px-4 border-b">Participant</th>
                  <th className="py-2 px-4 border-b">Last Activity</th>
                  <th className="py-2 px-4 border-b">Recent Completions (30d)</th>
                </tr>
              </thead>
              <tbody>
                {currentItems.map((row, index) => (
                  <tr key={index}>
                    <td className="py-2 px-4 border-b">{row.participantFullName}</td>
                    <td className="py-2 px-4 border-b">{row.lastActivityAt ? new Date(row.lastActivityAt).toLocaleDateString() : 'N/A'}</td>
                    <td className="py-2 px-4 border-b">{row.recentCompletions30d}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
          />
        </>
      ) : (
        <div className="text-center p-4">No Participant Activity data available.</div>
      )}
    </div>
  );
};

export default ParticipantActivityAnalytics;
